import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { Settings } from '../../_core/settings';
import { Core } from '../../_core/core';
import { HomeS } from '../../_core/home';
import { BrandingService } from '../../_core/branding.service';
import { ToastService } from '../../_core/toast.service';
import { Spinner } from '../../_core/spinner';

@Component({
  selector: 'app-tenant-details',
  imports: [
    CommonModule,
    RouterLink,
    CardModule,
    InputTextModule,
    FloatLabelModule,
    IconFieldModule,
    InputIconModule,
    ButtonModule,
    CheckboxModule,
    DialogModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './tenant-details.html',
  styleUrls: ['./tenant-details.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TenantDetailsComponent implements OnInit {
  public $settings = inject(Settings);
  private $core = inject(Core);
  private $home = inject(HomeS);
  private $branding = inject(BrandingService);
  private $toast = inject(ToastService);
  private $spinner = inject(Spinner);
  private router = inject(Router);

  public isLoading = signal(true);

  public tenantInfo = computed(() => this.$settings.tenantPostalInfo());

  public readonly isGGM = this.$branding.isGGM;
  public readonly sendRequestsEnabled = this.$branding.sendRequestsEnabled;

  public canUseReciboVerde = computed(() => {
    return this.$branding.isGGM() && this.$home.homeData()?.hasBilling === true;
  });

  public sendSettlementByEmail = computed(() => {
    return this.$home.homeData()?.hasElectronicSettlement ?? false;
  });

  public showPrivacyModal = false;
  public waControl: FormControl = new FormControl({
    value: false,
    disabled: this.$settings.phoneList().length === 0,
  });

  public phoneCtrl = new FormControl('', [Validators.required, Validators.pattern(/^[679]\d{8}$/)]);

  public canSave = computed(() => {
    const hasPhones = this.$settings.phoneList().length > 0;
    return hasPhones === this.$settings.receiveWhatsapp();
  });

  constructor() {
    effect(() => {
      this.waControl.setValue(this.$settings.receiveWhatsapp(), { emitEvent: false });
      if (this.$settings.phoneList().length === 0) {
        this.waControl.disable();
      } else {
        this.waControl.enable();
      }
    });
  }

  async ngOnInit(): Promise<void> {
    try {
      this.isLoading.set(true);

      if (!this.$home.homeData()) {
        await this.$home.fetchHomeData();
      }

      const building = this.$core.selectedBuilding();

      if (building?.tenantId) {
        const currentInfo = this.$settings.tenantPostalInfo();

        if (!currentInfo) {
          await this.$settings.fetchTenantPostalInfo(building.tenantId);
        }

        await this.$settings.getPhoneCommunications(building.tenantId);
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  public navigateToNotifications(): void {
    this.router.navigate(['/recibo-verde']);
  }

  public onWhatsappToggle(checked: boolean): void {
    if (checked) {
      this.waControl.setValue(false, { emitEvent: false });
      this.showPrivacyModal = true;
    } else {
      this.$settings.receiveWhatsapp.set(false);
    }
  }

  public acceptPrivacy(): void {
    this.waControl.setValue(true);
    this.$settings.receiveWhatsapp.set(true);
    this.showPrivacyModal = false;
  }

  public cancelPrivacy(): void {
    this.waControl.setValue(false);
    this.$settings.receiveWhatsapp.set(false);
    this.showPrivacyModal = false;
  }

  public addPhone(): void {
    if (this.phoneCtrl.invalid) {
      this.$toast.warning('Introduce un número válido (9 dígitos)');
      return;
    }

    const val = parseInt(this.phoneCtrl.value!, 10);

    if (this.$settings.phoneList().some((p) => p.phone === val)) {
      this.$toast.error('Este teléfono ya existe');
      return;
    }

    this.$settings.phoneList.update((list) => [...list, { id: null, phone: val }]);
    this.waControl.enable();
    this.phoneCtrl.reset();
  }

  public removePhone(index: number): void {
    this.$settings.phoneList.update((list) => {
      const newList = list.filter((_, i) => i !== index);
      if (newList.length === 0) {
        this.$settings.receiveWhatsapp.set(false);
      }
      return newList;
    });
    if (this.$settings.phoneList().length === 0) {
      this.waControl.disable();
    }
  }

  public async saveChanges(): Promise<void> {
    const data = this.$settings.phoneList();
    const tenantId = this.$core.selectedBuilding()?.tenantId;
    if (!tenantId) {
      return;
    }

    this.$spinner.show();
    try {
      const res = await this.$settings.postPhoneCommunications(tenantId, data);

      if (res) {
        await this.$home.fetchHomeData(true);
        this.$toast.success('Preferencias de comunicaciones actualizadas.');
      } else {
        this.$toast.error('Hubo un error al guardar los datos.');
      }
      this.$spinner.hide();
    } catch {
      this.$toast.error('Hubo un error al guardar los datos.');
      this.$spinner.hide();
    }
  }
}
